/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { useEffect, useState } from 'react'

import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { useAuth } from 'context/auth'
import { setMiniSidenav, setOrganisationAssets, useMaterialUIController } from 'context/materialUI'
import NotFound from 'layouts/not-found'
import Footer from 'modules/Footer'
import Sidenav from 'modules/Sidenav'
import { ROUTE_PATHS, RouteType } from 'routes'
import { getSubDomainAssets } from 'utils/subDomain'

const App = () => {
	const [controller, dispatch] = useMaterialUIController()
	const { miniSidenav, layout, routes } = controller
	const { user } = useAuth()
	const [onMouseEnter, setOnMouseEnter] = useState(false)
	const { pathname } = useLocation()

	const domain = window.location.hostname

	// Open sidenav when mouse enter on mini sidenav
	const handleOnMouseEnter = () => {
		if (miniSidenav && !onMouseEnter) {
			setMiniSidenav(dispatch, false)
			setOnMouseEnter(true)
		}
	}

	// Close sidenav when mouse leave mini sidenav
	const handleOnMouseLeave = () => {
		if (onMouseEnter) {
			setMiniSidenav(dispatch, true)
			setOnMouseEnter(false)
		}
	}

	// Setting page scroll to 0 when changing the route
	useEffect(() => {
		document.documentElement.scrollTop = 0
		document.scrollingElement.scrollTop = 0
	}, [pathname])

	useEffect(() => {
		const fetchImageAssets = async () => {
			const assets = await getSubDomainAssets(domain)
			if (assets) {
				setOrganisationAssets(dispatch, assets)
			} else {
				setOrganisationAssets(dispatch, null)
			}
		}

		fetchImageAssets()
	}, [dispatch, domain])

	const getRoutes = (allRoutes: RouteType[], routeType: 'auth' | 'protected' | 'other'): any =>
		allRoutes.map(route => {
			if (route.collapse) {
				return getRoutes(route.collapse, routeType)
			}

			//Only render the route if we specified the auth routes and the screens where auth is not required
			if (route.route && routeType === 'auth' && route.authRequired === false) {
				return <Route path={route.route} element={route.component} key={route.key} />
			}

			// Only render the route if we specified the protected routes and auth is required
			if (route.route && routeType === 'protected' && route.authRequired === true) {
				if (route.roleRequired && !route.roleRequired.includes(user?.data?.role)) {
					return null
				}
				return <Route path={route.route} element={route.component} key={route.key} />
			}

			if (route.route && routeType === 'other' && route.authRequired === undefined) {
				return <Route path={route.route} element={route.component} key={route.key} />
			}

			return null
		})

	const INIT_ROUTE = user ? ROUTE_PATHS.DASHBOARD.ANALYTICS : ROUTE_PATHS.AUTHENTICATION.SIGN_IN

	return (
		<>
			{layout === 'dashboard' && user && (
				<>
					<Sidenav
						user={{
							email: user.auth.email.slice(0, 15) + '...',
							role: user.data.role,
							name: user.data.name,
						}}
						color={'primary'}
						routes={routes}
						onMouseEnter={handleOnMouseEnter}
						onMouseLeave={handleOnMouseLeave}
					/>
				</>
			)}
			<Routes>
				<Route index element={<Navigate to={INIT_ROUTE} replace />} />
				{!user && getRoutes(routes, 'auth')}
				{user && getRoutes(routes, 'protected')}
				{getRoutes(routes, 'other')}
				{/* TODO: Further thought about how to handle NotFound and reroute them if not signed in */}
				<Route path="*" element={<NotFound />} />
			</Routes>
			{layout === 'dashboard' && user && <Footer />}
		</>
	)
}

export default App
